import { useState } from "react"
import {
  Card,
  Upload,
  Button,
  Space,
  Typography,
  message,
  Alert,
} from "@mimber/ui-web"
import { Image, Popconfirm } from "antd"
import { UploadOutlined, DeleteOutlined, PictureOutlined } from "@mimber/ui-web"
import type { UploadFile } from "@mimber/ui-web"
import {
  useUploadEventImage,
  useDeleteEventImage,
} from "@mimber/client/mimber-organization"
import type { EventImageUploadProps } from "./EventImageUpload.types"

const { Text, Paragraph } = Typography

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/jpg"]

export const EventImageUpload: React.FunctionComponent<
  EventImageUploadProps
> = ({ event, onSuccess, disabled = false }) => {
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const uploadImage = useUploadEventImage(event.uuid)
  const deleteImage = useDeleteEventImage(event.uuid)

  const beforeUpload = (file: File) => {
    const isValidType = ALLOWED_TYPES.includes(file.type)
    if (!isValidType) {
      message.error("You can only upload PNG or JPG files!")
      return Upload.LIST_IGNORE
    }

    const isValidSize = file.size <= MAX_FILE_SIZE
    if (!isValidSize) {
      message.error("File must be smaller than 5MB!")
      return Upload.LIST_IGNORE
    }

    return false // Prevent auto upload, handle manually
  }

  const handleUpload = () => {
    if (fileList.length === 0) {
      message.warning("Please select a file first")
      return
    }

    const file = fileList[0].originFileObj as File

    uploadImage.mutate(
      { file },
      {
        onSuccess: () => {
          message.success("Event image uploaded successfully")
          setFileList([])
          onSuccess?.()
        },
        onError: (error) => {
          message.error(error.message || "Failed to upload image")
        },
      }
    )
  }

  const handleDelete = () => {
    deleteImage.mutate(undefined, {
      onSuccess: () => {
        message.success("Event image deleted successfully")
        onSuccess?.()
      },
      onError: (error) => {
        message.error(error.message || "Failed to delete image")
      },
    })
  }

  const handleChange = (info: { fileList: UploadFile[] }) => {
    setFileList(info.fileList.slice(-1))
  }

  return (
    <Card
      title={
        <Space>
          <PictureOutlined />
          <span>Event Image</span>
        </Space>
      }
    >
      <Paragraph type="secondary">
        Upload an image for this event. This will be displayed on the event
        detail page. Supported formats: PNG, JPG (max 5MB).
      </Paragraph>

      {/* Current Image Display */}
      {event.imageStorageItem?.storageUrl && (
        <div style={{ marginBottom: 24 }} data-testid="current-image-section">
          <Text strong>Current Image:</Text>
          <div style={{ marginTop: 8 }}>
            <Image
              src={event.imageStorageItem.storageUrl}
              alt={`${event.title} image`}
              width={300}
              style={{ objectFit: "cover", borderRadius: 8 }}
              placeholder
              data-testid="current-event-image"
            />
          </div>
          <div style={{ marginTop: 8 }}>
            <Popconfirm
              title="Delete Image"
              description="Are you sure you want to delete the current event image?"
              onConfirm={handleDelete}
              okText="Yes"
              cancelText="No"
              okButtonProps={{ danger: true }}
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={deleteImage.isPending}
                disabled={disabled}
                data-testid="delete-image-button"
              >
                Delete Image
              </Button>
            </Popconfirm>
          </div>
        </div>
      )}

      {/* Upload Section */}
      <Space orientation="vertical" style={{ width: "100%" }}>
        <Alert
          message="Requirements"
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>Formats: PNG, JPG</li>
              <li>Maximum size: 5MB</li>
              <li>Recommended: 16:9 aspect ratio (e.g., 1280x720px)</li>
            </ul>
          }
          type="info"
          showIcon
        />

        <Upload.Dragger
          fileList={fileList}
          beforeUpload={beforeUpload}
          onChange={handleChange}
          maxCount={1}
          disabled={disabled || uploadImage.isPending}
          accept=".png,.jpg,.jpeg"
          data-testid="upload-dragger"
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <p className="ant-upload-text">
            Click or drag file to this area to upload
          </p>
          <p className="ant-upload-hint">
            Support for single file upload. Replace existing image by uploading
            a new one.
          </p>
        </Upload.Dragger>

        {fileList.length > 0 && (
          <Button
            type="primary"
            onClick={handleUpload}
            loading={uploadImage.isPending}
            disabled={disabled}
            icon={<UploadOutlined />}
            block
            data-testid="upload-image-button"
          >
            Upload Image
          </Button>
        )}
      </Space>
    </Card>
  )
}
